import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from './apiClient'; // Adjust path as needed

// Create Authentication Context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// API base URL - adjust this to match your Django server
const API_BASE_URL = 'http://localhost:8000';

// Authentication Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);

  // Check if user is already authenticated on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check authentication status
  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const sessionId = localStorage.getItem('sessionId');
      
      if (!token || !sessionId) {
        setIsLoading(false);
        return;
      }

      // Verify session with backend using API client
      const response = await apiClient.get('/api/frontend/auth/profile/');

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.user);
          setIsAuthenticated(true);
          setSessionData({
            sessionId: sessionId,
            token: token
          });
        } else {
          // Clear invalid session
          clearAuthData();
        }
      } else {
        // Clear invalid session
        clearAuthData();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (username, password, rememberMe = false) => {
    try {
      setIsLoading(true);
      
      // Use API client for login
      const response = await apiClient.post('/api/frontend/auth/login/', {
        username,
        password,
        remember_me: rememberMe
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store authentication data
        const sessionId = data.data.session.session_id;
        const userData = data.data.user;
        
        // Store in localStorage for persistence
        localStorage.setItem('sessionId', sessionId);
        localStorage.setItem('authToken', sessionId); // Using session_id as token
        localStorage.setItem('userData', JSON.stringify(userData));

        // Update state
        setUser(userData);
        setIsAuthenticated(true);
        setSessionData({
          sessionId: sessionId,
          expiresAt: data.data.session.expires_at,
          rememberMe: data.data.session.remember_me
        });

        return { success: true, user: userData };
      } else {
        return { 
          success: false, 
          error: data.error || data.detail || 'Login failed' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Network error. Please try again.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      
      // Use API client for logout
      await apiClient.post('/api/frontend/auth/logout/');

      // Clear auth data regardless of API response
      clearAuthData();
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local data even if API call fails
      clearAuthData();
      return { success: false, error: 'Logout failed' };
    } finally {
      setIsLoading(false);
    }
  };

  // Clear authentication data
  const clearAuthData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userData');
    setUser(null);
    setIsAuthenticated(false);
    setSessionData(null);
  };

  // Get user role data
  const getUserRole = () => {
    if (!user) return null;
    
    return {
      userType: user.user_type,
      roleData: user.role_data,
      operatorProfile: user.operator_profile,
      adminProfile: user.admin_profile
    };
  };

  // Check if user has specific permissions
  const hasPermission = (permission) => {
    if (!user) return false;
    
    if (user.user_type === 'SYSTEM_ADMIN' && user.admin_profile) {
      return user.admin_profile.permissions?.includes(permission) || false;
    }
    
    return false;
  };

  // Check if user is qualified (for operators)
  const isQualified = () => {
    if (!user) return false;
    
    if (user.user_type === 'OPERATOR' && user.operator_profile) {
      return user.operator_profile.is_qualified || false;
    }
    
    return true; // Admins are considered qualified
  };

  const contextValue = {
    user,
    isAuthenticated,
    isLoading,
    sessionData,
    login,
    logout,
    getUserRole,
    hasPermission,
    isQualified,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};